import { useCallback, useEffect, useMemo, useRef } from 'react'

import {
  AddLineItemDocument,
  BillingAddonReferenceFragment,
  CreateUpsellRequestDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'

import useCreateSubscriptionOverload from 'modules/discover/hooks/useCreateSubscriptionOverload'
import { isValidModule } from 'modules/marketplace_modules/lib/typesValidation'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'

type DatoModuleUpsellProps = {
  billingAddon: BillingAddonReferenceFragment | null
  moduleName: string
  currentUpsellOrigin: {
    request_source: string
    request_source_category?: string
    request_source_component?: string
  } | null
  featureId?: string
  subscriptionExpiresIn?: string
  chargebeeAddonId?: string
}

const useButtonActions = ({
  billingAddon,
  moduleName,
  currentUpsellOrigin,
  featureId,
  subscriptionExpiresIn,
  chargebeeAddonId,
}: DatoModuleUpsellProps) => {
  const { company } = useCurrentSession()
  const [addLineItem] = useMutation(AddLineItemDocument, {
    extractorFn: (data) => data.upsellMutations.createUpsellAddLineItem,
  })
  const { currentSeats, period, configPlan, allAddons, setConfigPlan, togglePlanSelect } =
    useSelfUpsellContext()

  const [createUpsell] = useMutation(CreateUpsellRequestDocument, {
    extractorFn: (data) => data.upsellMutations.createUpsellUpsellRequest,
  })

  const addon = useMemo(() => {
    return (
      allAddons.f25Addons.find((a) => a.internalTitle === billingAddon?.internalTitle) ||
      allAddons.f25Addons[0]
    )
  }, [allAddons.f25Addons, billingAddon?.internalTitle])

  const initializedRef = useRef(false)

  useEffect(() => {
    if (addon && !initializedRef.current) {
      initializedRef.current = true
      if (!addon.isSelected) togglePlanSelect(addon)
      setConfigPlan({
        addon: [addon.pricing.getPlanId(period) || ''],
        variant: addon.activeVariantType,
      })
    }
  }, [addon])

  const addLineItemMutation = useCallback(async () => {
    return await addLineItem({
      variables: {
        newAddons: Array.isArray(configPlan.addon)
          ? configPlan.addon.map((addonItem) => ({
              itemPriceId: addonItem || '',
              seats: currentSeats,
              module: moduleName,
            }))
          : [
              {
                itemPriceId: configPlan.addon || '',
                seats: currentSeats,
                module: moduleName,
              },
            ],
        origin: origin,
        feature: featureId || '',
      },
    })
  }, [configPlan, currentSeats, moduleName, addLineItem])

  const createSubscriptionOverload = useCreateSubscriptionOverload([company.id])

  const handleSubscriptionOverloadMutation = async () => {
    const today = new Date()

    const result = await createSubscriptionOverload({
      chargebeeId: chargebeeAddonId || '',
      overloadReason: 'GLP - Landing_page',
      newExpirationDate: new Date(
        today.setDate(today.getDate() + parseInt(subscriptionExpiresIn || '0'))
      ).toISOString(),
    })

    return result
  }

  const handleUpsellRequestMutation = async () => {
    const result = await createUpsell({
      variables: {
        requestReason: ValidReasons.MissingAddon,
        moduleRequested: isValidModule(moduleName) ? moduleName : undefined,
        requestSource: 'GLP - ' + currentUpsellOrigin?.request_source,
        requestSourceCategory: currentUpsellOrigin?.request_source_category,
        requestSourceComponent: currentUpsellOrigin?.request_source_component,
        upsellRequestComponentType: 'GLP Button',
      },
    })
    return result
  }

  return { addLineItemMutation, handleSubscriptionOverloadMutation, handleUpsellRequestMutation }
}

export default useButtonActions
